<template>
  <m-pageframe title="{organization-label}: Edit Order Confirmation Forms">
    <v-container
      fluid
      style="padding-left: 30px; padding-right: 30px;"
    >
      <widget-editor-layout
        v-if="settings"
        ref="widgetLayout"
        :settings_collapsed="left_pane_collapsed"
        :active_tab.sync="active_tab"
        :back_route="back_route"
        back_label="All Order Confirmation Forms"
        :is_published="is_published"
        :is_archived="settings.is_archived"
        :can_unarchive="can_unarchive"
        :item_label="settings.internal_label"
        :show_qr_code="false"
        :show_copy_url="false"
        archive_dialog_title="Archive Order Confirmation Form"
        :has_draft_changes="has_draft_changes"
        :publish_time="settings.publish_time"
        :publish_signature_id="settings.publish_signature_id"
        :change_descriptions="edit_status?.change_descriptions || []"
        :validation_warnings="validation_warnings"
        publish_dialog_title="Publish Order Confirmation Form"
        @archive="archiveSettings"
        @unarchive="unarchiveSettings"
        @publish="handlePublish"
        @discard-changes="handleDiscardChanges"
        @fix-warning="fixWarning"
      >
        <template #publish-agreement="{ publish_agreement_accepted, update_publish_agreement_accepted }">
          <m-legal-checkbox-agreement
            type="order-confirmation-settings-publication-agreement"
            :organization_id="$route.params.organization_id"
            :organization_label="settings ? settings.organization_label : ''"
            :value="publish_agreement_accepted"
            @input="update_publish_agreement_accepted"
            ref="publish-agreement"
            class="mb-4"
          />
        </template>

        <template #general-settings>
          <v-expansion-panels
            class="section-group mb-6"
            v-model="expanded_general"
            multiple
          >
            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-cog"
                title="Settings"
                subtitle="Configure the internal label, template, and basic options"
              />
              <v-expansion-panel-content class="pt-4 pb-2">
                <v-row class="mt-1">
                  <v-col cols="12">
                    <v-text-field
                      v-if="settings"
                      data-validation-target="formNameInput"
                      v-model="settings.internal_label"
                      label="Form Name"
                      hint="This is only used internally and is not visible to customers"
                      outlined
                      persistent-hint
                      dense
                      :rules="[rules.required]"
                      @input="onChange"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-message-text-outline"
                title="Messaging Settings"
                subtitle="Configure linked automated message, inbox, and after submission message"
              />
              <v-expansion-panel-content class="pt-2">
                <v-row class="mt-0 mb-4">
                  <v-col cols="12">
                    <div class="subtitle-2 font-weight-medium mb-2">Linked Automated Message</div>

                    <v-alert
                      v-if="automated_message_status"
                      :type="automated_message_status.type"
                      class="py-4 mb-2"
                      dense
                      text
                    >
                      <p class="mb-0">{{ automated_message_status.message }}</p>
                      <v-btn
                        v-if="automated_message_status.button"
                        :outlined="automated_message_status.type === 'success'"
                        :color="automated_message_status.button.color"
                        class="mt-4 mb-1"
                        small
                        @click="automated_message_status.button.action"
                      >
                        {{ automated_message_status.button.text }}
                      </v-btn>
                    </v-alert>
                  </v-col>
                </v-row>

                <v-row class="mt-1 mb-4">
                  <v-col
                    cols="12"
                    data-validation-target="updateOrderInboxSelect"
                  >
                    <select-inbox
                      v-model="settings.update_order_inbox_id"
                      :organization_id="$route.params.organization_id"
                      label="Inbox for Order Change Requests"
                      hint=""
                      hide_details
                      @change="onChange"
                      @inboxes-loaded="inbox_choices = $event"
                    >
                      <template #append-below>
                        <div v-if="inbox_choices.length" class="text-caption">
                          Don't have a dedicated inbox for Order Confirmations?
                          <router-link :to="inbox_creation_route">
                            Create one here
                          </router-link>
                        </div>
                      </template>
                    </select-inbox>

                    <v-alert
                      v-if="inbox_choices.length === 0"
                      type="info"
                      class="mt-5 mb-0"
                      outlined
                    >
                      <div class="text-body-2">
                        <strong>No inboxes available</strong>
                      </div>
                      <div class="text-body-2 mt-2">
                        An inbox is needed to receive change requests from customers. Please create an inbox first.
                      </div>
                      <v-btn
                        small
                        color="secondary"
                        class="mt-3"
                        :to="inbox_creation_route"
                      >
                        <v-icon
                          left
                          small
                        >mdi-inbox</v-icon>
                        Create Inbox
                      </v-btn>
                    </v-alert>
                  </v-col>
                </v-row>


                <v-switch
                  v-model="settings.no_double_opt_in_after_submission_message_enabled"
                  label="Send After Submission Message"
                  @change="onChange"
                  class="mb-4"
                />
                <div class="mt-4">
                  <m-edit-select-organization-owned-endpoint
                    ref="sendFromEndpointSelect"
                    data-validation-target="sendFromEndpointSelect"
                    :organization_id="$route.params.organization_id"
                    v-model="settings.send_from_endpoint"
                    label="Send messages from"
                    :include_bot_numbers="true"
                    @input="onChange"
                  />
                </div>
                <div>
                  <edit-message-patterns-compact
                    label="After submission message"
                    v-model="settings.no_double_opt_in_after_submission_message_patterns"
                    context="form-after-submit"
                    :default_value="get_default_no_double_opt_in_after_submission_message_patterns()
                      "
                    @input="onChange"
                    class="mb-4"
                    ref="afterSubmitInput"
                    data-validation-target="afterSubmitInput"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-connection"
                title="Integrations"
                subtitle="Connect with RedChirp lists and external CRM systems"
              />
              <v-expansion-panel-content class="py-4">
                <h2 class="subtitle-1 font-weight-medium mb-3">CRM Integration Settings</h2>
                <div class="px-md-4">
                  <m-compatible-integrations-hint
                    feature-identifier="customers-auto-enrich-from-form-submission"
                    before-link-text="Customer auto-enrichment from order confirmation submissions requires a"
                    link-text="compatible CRM integration"
                    after-link-text="to be enabled for your organization."
                    modal-title="Compatible CRM Integrations"
                    modal-description="Customer enrichment from order confirmation submissions is currently supported for the following CRM integrations:"
                    no-integrations-message="No compatible CRM integrations are currently enabled for your organization."
                    footer-message="More integrations will be added in the future. If your CRM is not listed above, the order confirmation form will still function but customer data will not be automatically synced to your CRM."
                    :show-hint="!customer_auto_enrich_feature_available"
                  />
                  <m-edit-select-enum
                    label="On Form Submission"
                    enum_name="crm_customer_upsert_mode"
                    v-model="settings.on_submit_crm_customer_upsert_mode"
                    @input="onChange"
                    :read_only="!customer_auto_enrich_feature_available"
                    hint="Action to take in your CRM when a customer submits the form."
                    class="mb-4"
                  />
                  <v-select
                    v-if="show_tag_selection"
                    label="Tag for New Customers"
                    v-model="settings.new_customer_tag_id"
                    :items="integration_tags_with_none"
                    item-text="tag_label"
                    item-value="tag_id"
                    clearable
                    :disabled="!customer_auto_enrich_feature_available"
                    hint="Select a manual tag to automatically apply to newly created customers from order confirmation submissions. This tag will NOT be applied to existing customers. Select 'No Tag' to disable tagging."
                    persistent-hint
                    class="mt-3"
                    @change="onNewCustomerTagChange"
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>No tags available</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </div>
                <h2 class="subtitle-1 font-weight-medium">
                  RedChirp Integration Settings
                </h2>
                <div class="px-md-4">
                  <v-select
                    :label="settings.double_opt_in_state === 'enabled'
                      ? 'After double opt-in, add contact to list(s)'
                      : 'On submission, add contact to list(s)'
                      "
                    v-model="settings.add_to_lists"
                    :items="available_lists"
                    item-text="text"
                    item-value="value"
                    @change="onChange"
                    multiple
                    chips
                    clearable
                    persistent-hint
                    hint="At minimum, you must have a phone number in the form submission to add the customer to a list. Only customers who provide a phone number with consent will be added to lists."
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>No lists available</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-text"
                title="Agreement"
                subtitle="Manage initial consent gathering and agreement text"
              />
              <v-expansion-panel-content>
                <div class="d-flex align-center mt-4">
                  <h2 class="subtitle-1 font-weight-medium sr-only">Consent Gathering</h2>
                </div>
                <div class="mt-0">
                  <v-card flat>
                    <v-card-title class="text-h6 pl-0 pt-1 pb-2">
                      <v-icon left>mdi-email</v-icon>
                      Email Agreement
                    </v-card-title>
                    <v-card-text>
                      <m-edit-select-enum
                        label="Agreement Type"
                        v-model="settings.email_consent_agreement_type"
                        enum_name="consent_agreement_type"
                        dialog_title="Select Agreement Type"
                        @change="onChange"
                      />
                      <div
                        v-if="settings.email_consent_agreement_type === 'custom'"
                        class="mb-4"
                      >
                        <edit-message-patterns-compact
                          label="Custom Agreement Patterns"
                          v-model="settings.email_consent_agreement_custom_patterns"
                          hint="Custom patterns for email agreement text. Use variables like {organization-label}, {terms-link}, {privacy-policy-link}"
                          :default_value="getDefaultEmailAgreementPattern()
                            ? [getDefaultEmailAgreementPattern()]
                            : null
                            "
                          @input="onChange"
                        />
                      </div>
                      <div
                        v-else
                        class="mb-4"
                      >
                        <v-alert
                          type="info"
                          dense
                          text
                          class="mt-2"
                        >
                          Using default agreement pattern. The resolved text is shown in the
                          preview.
                        </v-alert>
                      </div>
                      <m-edit-select-enum
                        ref="emailConsentModeSelect"
                        data-validation-target="emailConsentModeSelect"
                        label="Consent Gathering Mode"
                        v-model="settings.email_consent_gathering_state"
                        enum_name="form_consent_gathering_state"
                        dialog_title="Select Consent Gathering Mode"
                        @change="onEmailConsentGatheringChange"
                        :filter_options="filtered_email_consent_gathering_options"
                      />
                      <m-edit-css
                        label="Email Agreement"
                        v-model="settings.email_agreement_style.style"
                        :default_value="settings.email_agreement_style.default_style"
                        :mobile_value.sync="settings.email_agreement_style.mobile_styles"
                        :default_mobile_value="settings.email_agreement_style.default_mobile_styles"
                        class="mb-4"
                      />
                    </v-card-text>
                  </v-card>

                  <v-divider class="mt-3 mb-6"></v-divider>

                  <v-card flat>
                    <v-card-title class="text-h6 pl-0 pt-1 pb-2">
                      <v-icon left>mdi-phone</v-icon>
                      Phone Number Agreement
                    </v-card-title>
                    <v-card-text>
                      <m-edit-select-enum
                        label="Agreement Type"
                        v-model="settings.phone_number_consent_agreement_type"
                        enum_name="consent_agreement_type"
                        dialog_title="Select Agreement Type"
                        @change="onChange"
                      />
                      <div
                        v-if="settings.phone_number_consent_agreement_type === 'custom'"
                        class="mb-4"
                      >
                        <edit-message-patterns-compact
                          label="Custom Agreement Patterns"
                          v-model="settings.phone_number_consent_agreement_custom_patterns"
                          hint="Custom patterns for phone agreement text. Use variables like {organization-label}, {terms-link}, {privacy-policy-link}"
                          :default_value="getDefaultPhoneAgreementPattern()
                            ? [getDefaultPhoneAgreementPattern()]
                            : null
                            "
                          @input="onChange"
                        />
                      </div>
                      <div
                        v-else
                        class="mb-4"
                      >
                        <v-alert
                          type="info"
                          dense
                          text
                          class="mt-2"
                        >
                          Using default agreement pattern. The resolved text is shown in the
                          preview.
                        </v-alert>
                      </div>
                      <m-edit-select-enum
                        ref="phoneConsentModeSelect"
                        data-validation-target="phoneConsentModeSelect"
                        label="Consent Gathering Mode"
                        v-model="settings.phone_number_consent_gathering_state"
                        enum_name="form_consent_gathering_state"
                        dialog_title="Select Consent Gathering Mode"
                        @change="onPhoneConsentGatheringChange"
                        :filter_options="filtered_phone_consent_gathering_options"
                      />
                      <m-edit-css
                        label="Phone Number Agreement"
                        v-model="settings.phone_number_agreement_style.style"
                        :default_value="settings.phone_number_agreement_style.default_style"
                        :mobile_value.sync="settings.phone_number_agreement_style.mobile_styles"
                        :default_mobile_value="settings.phone_number_agreement_style.default_mobile_styles
                          "
                        class="mb-4"
                      />
                    </v-card-text>
                  </v-card>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <template #content-styles>
          <v-expansion-panels
            v-if="settings"
            v-model="expanded_styles"
            multiple
            class="section-group mb-6"
          >
            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-view-dashboard"
                title="Container & Layout"
                subtitle="Configure form layout, container styling, and background effects"
              />
              <v-expansion-panel-content class="pt-4">
                <div class="px-md-4">
                  <m-edit-select-enum
                    v-model="settings.layout_mode"
                    enum_name="layout_style"
                    label="Layout Mode"
                    dialog_title="Select Layout Style"
                    @change="onChange"
                  />
                  
                  <widget-editor-reapply-template
                    class="mt-2 mb-6"
                    :form_id="$route.params.order_confirmation_settings_id"
                    template_selection_route="order-confirmation-template-selection"
                  />

                  <m-edit-css
                    label="Background Blur"
                    subheader="The blurred overlay that appears on top of the page background image"
                    v-model="settings.background_blur.style"
                    :default_value="settings.background_blur.default_style"
                    :mobile_value.sync="settings.background_blur.mobile_styles"
                    :default_mobile_value="settings.background_blur.default_mobile_styles"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    label="Container"
                    :subheader="is_side_by_side_layout ? 'The wrapper that holds all content, including the image and form' : 'The main form container that holds all content and input fields'"
                    v-model="settings.container.style"
                    :default_value="settings.container.default_style"
                    :mobile_value.sync="settings.container.mobile_styles"
                    :default_mobile_value="settings.container.default_mobile_styles"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    v-if="is_side_by_side_layout"
                    label="Container Content"
                    :subheader="`The inner content area that contains the form ${is_side_by_side_layout ? '(the right panel on larger screens)' : ''}`"
                    v-model="settings.container_content.style"
                    :default_value="settings.container_content.default_style"
                    :mobile_value.sync="settings.container_content.mobile_styles"
                    :default_mobile_value="settings.container_content.default_mobile_styles"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    label="Form Containers"
                    subheader="The elements containing the order data on the initial order confirmation screen"
                    v-model="settings.form_containers.style"
                    :default_value="settings.form_containers.default_style"
                    :mobile_value.sync="settings.form_containers.mobile_styles"
                    :default_mobile_value="settings.form_containers.default_mobile_styles"
                    hint="Styling for form input containers and sections"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-format-header-1"
                title="Header & Content"
                subtitle="Customize header text and content lines"
              />
              <v-expansion-panel-content class="mt-6 pt-2 pb-0">
                <div class="px-md-4">
                  <m-edit-styled-text
                    label="Initial Title Text"
                    v-model="settings.initial_title_text"
                    hint="Main title text displayed on the order confirmation form"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Initial Subtitle Text"
                    v-model="settings.initial_subtitle_text"
                    hint="Subtitle text displayed below the main title"
                    @change="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-image-multiple"
                title="Images & Media"
                :subtitle="`Manage logo${is_side_by_side_layout ? ', side image, and background image' : ' and background image'}`"
              />
              <v-expansion-panel-content class="pt-2">
                <div class="mt-4 px-md-4">
                  <v-switch
                    v-model="settings.use_logo"
                    label="Show Logo"
                    color="primary"
                    class="mb-4"
                    hide-details
                    @change="onChange"
                  />
                  <transition name="fade">
                    <div
                      v-if="settings.use_logo"
                    >
                      <div class="sr-only">Logo Image</div>
                      <m-image-selector
                        ref="logoImageInput"
                        data-validation-target="logoImageInput"
                        v-model="settings.logo_image.url"
                        :organization_id="settings.organization_id"
                        label="Logo Image (Optional)"
                        @input="onChange"
                      />
                      <m-edit-css
                        label="Logo Image"
                        v-model="settings.logo_image.style"
                        :default_value="settings.logo_image.default_style"
                        :mobile_value.sync="settings.logo_image.mobile_styles"
                        :default_mobile_value="settings.logo_image.default_mobile_styles"
                        @input="onChange"
                        @mobileValueUpdated="onChange"
                      />
                    </div>
                  </transition>

                  <div v-if="is_side_by_side_layout" class="mb-6">
                    <div class="sr-only">Side Image</div>
                    <m-image-selector
                      v-model="settings.side_image.url"
                      :organization_id="$route.params.organization_id"
                      label="Side Image"
                      class="image-selector mb-2"
                      hint="Recommended: JPG with aspect ratio of 7:12 (e.g. 700px width x 1200px height)"
                      @input="onChange"
                    />
                    <m-edit-css
                      label="Side Image"
                      v-model="settings.side_image.style"
                      :default_value="settings.side_image.default_style"
                      :mobile_value.sync="settings.side_image.mobile_styles"
                      :default_mobile_value="settings.side_image.default_mobile_styles"
                      @input="onChange"
                      @mobileValueUpdated="onChange"
                    />
                  </div>
                  <div class="mb-4">
                    <div class="sr-only">Page Background Image</div>
                    <m-image-selector
                      v-model="settings.page_background.url"
                      :organization_id="$route.params.organization_id"
                      label="Page Background Image"
                      class="image-selector mb-2"
                      hint="Recommended: JPG with aspect ratio of 4:3 (e.g. 1600px width x 1200px height)"
                      @input="onChange"
                    />
                  <m-edit-css
                    label="Page Background Image"
                    v-model="settings.page_background.style"
                    :default_value="settings.page_background.default_style"
                    :mobile_value.sync="settings.page_background.mobile_styles"
                    :default_mobile_value="settings.page_background.default_mobile_styles"
                    class="mt-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-button-cursor"
                title="Buttons & Actions"
                subtitle="Style confirmation buttons and action elements"
              />
              <v-expansion-panel-content class="pt-8 pb-4">
                <div class="px-md-4">
                  <m-edit-styled-text
                    label="Check All Button Text"
                    v-model="settings.check_all_button_text"
                    hint="Text displayed on the button to check all items"
                    @change="onChange"
                  />
                  <m-edit-css
                    label="Check All Button"
                    class="mb-12"
                    v-model="settings.check_all_button.style"
                    :default_value="settings.check_all_button.default_style"
                    :mobile_value.sync="settings.check_all_button.mobile_styles"
                    :default_mobile_value="settings.check_all_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />

                  <m-edit-styled-text
                    label="Confirm Button Text"
                    v-model="settings.confirm_button_text"
                    hint="Text displayed on the main confirmation button"
                    @change="onChange"
                  />
                  <m-edit-css
                    label="Confirm Button"
                    class="mb-12"
                    v-model="settings.confirm_button.style"
                    :default_value="settings.confirm_button.default_style"
                    :mobile_value.sync="settings.confirm_button.mobile_styles"
                    :default_mobile_value="settings.confirm_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />

                  <m-edit-styled-text
                    label="Request Changes Button Text"
                    v-model="settings.update_order_button_text"
                    hint="Text displayed on the order update request button"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-css
                    label="Request Changes Button"
                    class="mb-12"
                    v-model="settings.update_order_button.style"
                    :default_value="settings.update_order_button.default_style"
                    :mobile_value.sync="settings.update_order_button.mobile_styles"
                    :default_mobile_value="settings.update_order_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />

                  <m-edit-styled-text
                    label="Back to Confirmation Button Text"
                    v-model="settings.back_to_confirmation_button_text"
                    hint="Text displayed on the button to go back to the confirmation page"
                    @change="onChange"
                  />
                  <m-edit-css
                    label="Back to Confirmation Button"
                    class="mb-12"
                    v-model="settings.back_to_confirmation_button.style"
                    :default_value="settings.back_to_confirmation_button.default_style"
                    :mobile_value.sync="settings.back_to_confirmation_button.mobile_styles"
                    :default_mobile_value="settings.back_to_confirmation_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />

                  <m-edit-styled-text
                    label="Submit Changes Button Text"
                    v-model="settings.submit_changes_button_text"
                    hint="Text displayed on the button to submit requested changes"
                    @change="onChange"
                  />
                  <m-edit-css
                    label="Submit Changes Button"
                    v-model="settings.submit_changes_button.style"
                    :default_value="settings.submit_changes_button.default_style"
                    :mobile_value.sync="settings.submit_changes_button.mobile_styles"
                    :default_mobile_value="settings.submit_changes_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-pencil-box"
                title="Update Order Section"
                subtitle="Configure the order update request section content"
              />
              <v-expansion-panel-content class="pt-2">
                <div class="mt-5 px-md-4">
                  <m-edit-styled-text
                    label="Update Order Title"
                    v-model="settings.update_order_title"
                    hint="Title text for the order update section"
                    class="mb-4"
                    @change="onChange"
                  />
                  <m-edit-styled-text
                    label="Update Order Subtitle"
                    v-model="settings.update_order_subtitle"
                    hint="Subtitle text explaining the order update process"
                    class="mb-4"
                    @change="onChange"
                  />
                  <m-edit-styled-text
                    label="Update Order Disclaimer"
                    v-model="settings.update_order_disclaimer"
                    hint="Disclaimer text for the order update section"
                    @change="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-check-circle"
                title="Thank You Page"
                subtitle="Customize the confirmation success page"
              />
              <v-expansion-panel-content class="pt-2">
                <div class="mt-4 px-md-4">
                  <h2 class="subtitle-1 font-weight-medium mb-4">No Changes Requested</h2>
                  <m-edit-styled-text
                    label="Thank You Title (No Changes)"
                    v-model="settings.thank_you_no_changes_title"
                    hint="Title shown when customer confirms order without requesting changes"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Thank You Subtitle (No Changes)"
                    v-model="settings.thank_you_no_changes_subtitle"
                    hint="Subtitle shown when customer confirms order without requesting changes"
                    @change="onChange"
                    class="mb-4"
                  />

                  <h2 class="subtitle-1 font-weight-medium mb-4 mt-6">Changes Requested</h2>
                  <m-edit-styled-text
                    label="Thank You Title (Changes Requested)"
                    v-model="settings.thank_you_changes_requested_title"
                    hint="Title shown when customer requests order changes"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Thank You Subtitle (Changes Requested)"
                    v-model="settings.thank_you_changes_requested_subtitle"
                    hint="Subtitle shown when customer requests order changes"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Container Text (Changes Requested)"
                    v-model="settings.thank_you_changes_requested_container_text"
                    hint="Container text explaining the change request process"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Response Text (Changes Requested)"
                    v-model="settings.thank_you_changes_requested_response_text"
                    hint="Text explaining how the business will respond to change requests"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    ref="contactTextChangesRequestedInput"
                    data-validation-target="contactTextChangesRequestedInput"
                    label="Contact Text (Changes Requested)"
                    v-model="settings.thank_you_changes_requested_contact_text"
                    hint="Contact information for customers with questions about changes"
                    @change="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-account-check"
                title="Age Verification"
                subtitle="Style age verification elements"
              />
              <v-expansion-panel-content class="pt-2">
                <div class="mt-2 px-md-4">
                  <v-row>
                    <v-col cols="12">
                      <v-checkbox
                        v-model="settings.age_verification_enabled"
                        label="Enable Age Verification"
                        hint="Require customers to verify they are of legal age"
                        persistent-hint
                        hide-details="auto"
                        class="mt-0"
                        @change="onChange"
                      />
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <m-edit-styled-text
                        label="Age Verification Message"
                        v-model="settings.age_verification_message"
                        class="mt-1"
                        @change="onChange"
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <template #preview>
          <widget-preview-panel
            ref="previewPanel"
            :preview_mode="preview_mode"
            :is_fullscreen="left_pane_collapsed"
            :is_loading="is_preview_loading"
            :dimensions="preview_dimensions"
            :initial_size_mode="use_actual_size"
            @fullscreen-toggle="left_pane_collapsed = !left_pane_collapsed"
            @size-mode-change="use_actual_size = $event"
          >
            <template #floating-controls>
              <widget-editor-floating-preview-control
                :visible="floating_control_visible"
                :preview_mode="preview_mode"
                :current_page="current_page"
                :pages="order_confirmation_pages"
                :view_modes="['mobile', 'tablet', 'tablet-landscape', 'desktop']"
                supports_test_data
                @preview-mode-change="onPreviewModeChange"
                @page-change="onPageChange"
                @auto-fill-test-data="() => onAutoFillTestData()"
                @dismiss="onDismissFloatingControl"
              />
            </template>

            <template #preview>
              <iframe
                v-if="settings && settings.order_confirmation_settings_publication_uuid"
                :key="preview_nonce"
                :src="previewSrc"
                class="preview-iframe"
                frameborder="0"
                @load="onPreviewLoad"
                ref="preview-iframe"
              />
              <div
                v-else
                class="preview-placeholder"
              >
                <v-icon
                  size="48"
                  color="grey lighten-1"
                >mdi-form-select</v-icon>
                <div class="text-caption text--secondary mt-2">
                  Save form to see preview
                </div>
              </div>
            </template>
          </widget-preview-panel>
        </template>
      </widget-editor-layout>
    </v-container>

  </m-pageframe>
</template>

<script>
import WidgetEditorLayout from '../../components/widget/widget-editor-layout.vue';
import WidgetPreviewPanel from '../../components/widget/widget-preview-panel.vue';
import WidgetEditorFloatingPreviewControl from '../../components/widget/widget-editor-floating-preview-control.vue';
import WidgetEditorReapplyTemplate from '../../components/widget/widget-editor-reapply-template.vue';
import ExpansionPanelHeaderRich from '../../components/shared/expansion-panels/expansion-panel-header-rich.vue';
import EditMessagePatternsCompact from '@/components/input/edit-message-patterns-compact.vue';
import MEditCss from '@/components/input/m-edit-css.vue';
import mEditStyledText from '@/components/input/m-edit-styled-text.vue';
import MImageSelector from '@/components/input/image-selector.vue';
import SelectInbox from '@/components/input/select-inbox.vue';
import { composeOrderConfirmationPreviewParams, sendPageChangeToWidget, sendSettingsToWidget, sendAutoFillTestData } from '@/js/widget/order-confirmation-preview-composer.js';
import { buildWidgetUrl } from '@/js/widget/widget-url-builder.js';
import { createParentMessageHandler, WIDGET_TO_PARENT } from '@/js/widget/widget-messaging.js';
import GuidedValidationMixin from '@/mixins/guided-validation.js';
import WidgetEditorStatePersistence from '@/mixins/widget/widget-editor-state-persistence.js';

export default {
  name: 'OrderConfirmationEdit',
  mixins: [GuidedValidationMixin, WidgetEditorStatePersistence],

  components: {
    WidgetEditorLayout,
    WidgetPreviewPanel,
    WidgetEditorFloatingPreviewControl,
    WidgetEditorReapplyTemplate,
    ExpansionPanelHeaderRich,
    EditMessagePatternsCompact,
    MEditCss,
    mEditStyledText,
    MImageSelector,
    SelectInbox,
  },

  data() {
    return {
      editor_state_key: this.$route.params.order_confirmation_settings_id, // for mixin
      first_load_complete: false,
      settings: null,
      edit_status: null,
      validation_warnings: [],
      left_pane_collapsed: false,
      active_tab: 0,
      expanded_general: [0],
      expanded_styles: [0],
      current_page: 'order-confirmation', // order-confirmation, request-changes, thanks-no-changes, thanks-with-changes
      preview_mode: 'mobile',
      floating_control_visible: true,
      save_debounce_handle: null,
      saving: false,
      inbox_choices: [],
      suppress_autosave: true,
      is_preview_loading: false,
      use_actual_size: 0,
      preview_nonce: 0,
      order_confirmation_settings_id: this.$route.params.order_confirmation_settings_id,
      rules: {
        required: value => !!value || 'This field is required',
      },
      default_legal_patterns: {
        double_opt_in_pattern: '',
        after_double_opt_in_message: '',
        email_agreement_implied: '',
        email_agreement_checkbox: '',
        phone_agreement_implied: '',
        phone_agreement_checkbox: '',
      },
      available_lists: [],
      customer_auto_enrich_feature_available: false,
      tag_new_form_customers_available: false,
      integration_tags: [],
      order_confirmation_id: this.$route.params.order_confirmation_id,
      automated_message_template: null,
      automated_message_template_error: false,
      widget_message_handler: null,
    };
  },

  computed: {
    is_side_by_side_layout() {
      return this.settings?.layout_mode === 'side-by-side' || false;
    },

    back_route() {
      return {
        name: 'search-forms-order-confirmation',
        params: { organization_id: this.$route.params.organization_id },
      };
    },

    inbox_creation_route() {
      return {
        name: 'edit-inboxes',
        params: { organization_id: this.$route.params.organization_id },
        query: { create_inbox_type: 'order-confirmation', conf_form_id: this.order_confirmation_settings_id }
      }
    },

    order_confirmation_pages() {
      return [
        { value: 'order-confirmation', label: 'Order Confirmation', icon: 'mdi-form-select' },
        { value: 'request-changes', label: 'Request Changes', icon: 'mdi-pencil-box' },
        { value: 'thanks-no-changes', label: 'Thank You (No Changes)', icon: 'mdi-heart-minus' },
        { value: 'thanks-with-changes', label: 'Thank You (With Changes)', icon: 'mdi-heart-plus' },
      ];
    },

    is_published() {
      return this.edit_status?.has_published_version;
    },

    automated_message_linked() {
      return this.automated_message_template &&
        this.automated_message_template.order_confirmation_settings_id === this.order_confirmation_settings_id;
    },

    automated_message_exists_but_not_linked() {
      return this.automated_message_template &&
        (!this.automated_message_template.order_confirmation_settings_id ||
          this.automated_message_template.order_confirmation_settings_id !== this.order_confirmation_settings_id);
    },

    automated_message_does_not_exist() {
      return this.automated_message_template_error || !this.automated_message_template;
    },

    has_draft_changes() {
      if (!this.edit_status) return false;
      return this.edit_status.has_unpublished_changes;
    },

    can_unarchive() {
      return this.settings?.is_archived && this.$rc.$state.isOrganizationAdmin();
    },

    public_url() {
      if (!this.edit_status?.has_published_version) {
        return '';
      }
      if (!this.settings?.short_id) return '';
      return `${window.location.origin}/app/order-confirmation/${this.settings.short_id}`;
    },

    publicUrlHint() {
      if (!this.is_published) {
        return 'URL will be available after publishing';
      }
      return 'This is the public URL customers will use';
    },

    widget_props() {
      if (!this.settings) return {};

      return {
        settings: this.settings,
        is_test_submission: true,
        preview_mode: this.preview_mode,
      };
    },

    preview_dimensions() {
      const dimensions = {
        mobile: { width: 410, height: 844 },
        tablet: { width: 1024, height: 1366 },
        'tablet-landscape': { width: 1366, height: 1024 },
      };
      return dimensions[this.preview_mode] || dimensions['tablet-landscape'];
    },

    previewSrc() {

      if (!this.settings || !this.settings.order_confirmation_settings_publication_uuid) {

        return null;
      }

      const params = {};
      if (this.preview_mode === 'mobile') {
        params.mobile = 'true';
      }

      const url = buildWidgetUrl('order-confirmation', params, 5176);
      return url;
    },

    filtered_email_consent_gathering_options() {
      //Email can choose all
      return ['consent-via-field-submission', 'checkbox-consent', 'none'];
    },

    filtered_phone_consent_gathering_options() {

      return ['checkbox-consent', 'none'];
    },

    integration_tags_with_none() {
      const no_tag_option = {
        tag_id: null,
        tag_label: 'No Tag',
      };
      return [no_tag_option, ...this.integration_tags];
    },

    show_tag_selection() {
      if (!this.tag_new_form_customers_available) {
        return false;
      }

      const creates_customers_on_submit =
        this.settings &&
        (this.settings.on_submit_crm_customer_upsert_mode === 'create-only' ||
          this.settings.on_submit_crm_customer_upsert_mode === 'create-or-update');

      const creates_customers_after_opt_in =
        this.settings &&
        (this.settings.after_double_opt_in_crm_customer_upsert_mode === 'create-only' ||
          this.settings.after_double_opt_in_crm_customer_upsert_mode === 'create-or-update');

      return creates_customers_on_submit || creates_customers_after_opt_in;
    },

    automated_message_status() {
      if (!this.is_published) {
        return {
          type: 'info',
          message: 'This Order Confirmation form cannot be linked to an automated message until it has been published.',
        };
      }

      if (this.automated_message_linked) {
        return {
          type: 'success',
          message: 'This form is linked to your "Order Placed: Request Confirmation and Opt-In (Uses Order Confirmation Form)" automated message template',
          button: {
            text: 'View Linked Automated Message',
            color: 'success',
            action: () => this.navigateToAutomatedMessageTemplate(),
          },
        };
      }

      if (this.automated_message_exists_but_not_linked) {
        return {
          type: 'info',
          message: 'The "Order Placed: Request Confirmation and Opt-In (Uses Order Confirmation Form)" automated message template exists, but it is not configured to use this form.',
          button: {
            text: 'View Automated Message Template',
            color: 'secondary',
            action: () => this.navigateToAutomatedMessageTemplate(),
          },
        };
      }

      if (this.automated_message_does_not_exist) {
        return {
          type: 'info',
          message: 'No automated message exists for "Order Placed: Request Confirmation and Opt-In (Uses Order Confirmation Form)" yet. Create one in order to send order confirmations to customers.',
          button: {
            text: 'Create Automated Message',
            color: 'secondary',
            action: () => this.navigateToAutomatedMessagesSettings(),
          },
        };
      }

      return null;
    },
  },

  beforeRouteLeave(to, from, next) {
    const page_exceptions = [
      'order-confirmation-template-selection',
      'edit-inboxes',
      'auto-messages-settings',
      'auto-messages-template-settings'
    ];
    if (page_exceptions.includes(to.name)) {
      next();
      return;
    }

    if (this.$refs.widgetLayout) {
      this.$refs.widgetLayout.confirmLeaveWithUnpublishedChanges(next);
    } else {
      next();
    }
  },

  watch: {
    settings: {
      handler() {
        this.onChange();
      },
      deep: true,
    },
  },

  mounted() {
    this.loadFormAndEditStatus();
    this.loadDefaultLegalPatterns();
    this.loadAvailableLists();
    this.loadIntegrationFeatures();
    this.getOrderConfAutomatedMessage();
    this.setupWidgetMessageListener();
    this.loadIntegrationData();
  },

  beforeUnmount() {
    this.cleanupWidgetMessageListener();
  },

  methods: {
    loadIntegrationData() {
      this.$rc.hasEnabledIntegrationFeature(
        'order-confirmation',
        is_supported => {
          this.has_necessary_integration = is_supported;
        },
        () => {
          this.has_necessary_integration = false;
        }
      );
    },

    setupWidgetMessageListener() {
      this.widget_message_handler = createParentMessageHandler({
        [WIDGET_TO_PARENT.READY]: () => {
          const should_recycle_test_data = this.first_load_complete;
          this.onAutoFillTestData(should_recycle_test_data);

          if (!this.first_load_complete) {
            this.first_load_complete = true;
          }
        },
        // keeps current_page in sync when user navigates within the preview iframe
        [WIDGET_TO_PARENT.PAGE_CHANGED]: (data) => {
          // eslint-disable-next-line no-console
          console.log('[OC Editor] Received PAGE_CHANGED from widget:', data);
          if (data?.page) {
            this.current_page = data.page;
          }
        },
      });
      window.addEventListener('message', this.widget_message_handler);
    },

    cleanupWidgetMessageListener() {
      if (this.widget_message_handler) {
        window.removeEventListener('message', this.widget_message_handler);
        this.widget_message_handler = null;
      }
    },

    loadFormAndEditStatus() {
      this.suppress_autosave = true;
      this.$rc.rest({
        spinner: true,
        endpoint: 'GET: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/edit-status',
        params: {
          organization_id: this.$route.params.organization_id,
          order_confirmation_settings_id: this.$route.params.order_confirmation_settings_id,
        },
        on_success: data => {
          this.edit_status = { ...data };
          this.settings = { ...data.draft_form };
          this.validateForm();
          this.$nextTick(() => {
            this.suppress_autosave = false;
          });
        },
        on_error: 'Failed to load order confirmation form',
      });
    },

    onChange() {
      if (this.suppress_autosave) {
        return;
      }

      this.validateForm();

      if (this.save_debounce_handle) {
        clearTimeout(this.save_debounce_handle);
      }
      this.save_debounce_handle = setTimeout(() => {
        if (this.settings) {
          this.autoSave();
        }
        this.preview_nonce = Date.now();

        this.$nextTick(() => {
          this.sendFormDataToWidget();
        });
      }, 750);
    },

    autoSave() {
      if (!this.settings || this.suppress_autosave || this.saving) return;
      this.saving = true;
      this.is_preview_loading = true;

      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/order-confirmation/settings',
        params: this.settings,
        silent: true,
        on_success: data => {
          if (this.save_debounce_handle) {
            clearTimeout(this.save_debounce_handle);
            this.save_debounce_handle = null;
          }

          this.suppress_autosave = true;
          if (this.settings && data) {
            Object.keys(data).forEach(k => {
              this.$set(this.settings, k, data[k]);
            });
          }

          this.$nextTick(() => {
            this.suppress_autosave = false;
            this.saving = false;
            this.is_preview_loading = false;
            this.loadFormAndEditStatus();
          });
        },
        on_error: () => {
          this.saving = false;
          this.is_preview_loading = false;
          if (this.save_debounce_handle) {
            clearTimeout(this.save_debounce_handle);
            this.save_debounce_handle = null;
          }
        },
      });
    },

    archiveSettings() {
      this.$rc.rest({
        spinner: true,
        endpoint: 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/archive',
        params: {
          order_confirmation_settings_id: this.order_confirmation_settings_id,
        },
        on_success: () => {
          window.location.reload();

        },
        on_error: (error) => {
          //On okay of alert refresh the page
          this.$rc.alert('Error', error.message || 'Unable to archive settings', () => {
            window.location.reload();
          });
        },
      });
    },

    unarchiveSettings() {
      this.$rc.confirm({
        title: `Unarchive Settings`,
        body: `Are you sure you want to unarchive the draft version of the form: "${this.settings ? this.settings.internal_label : 'Unknown'
          }"? <br/><br/>This will not unarchive any published version of the form.`,
        btn_text_ok: 'Unarchive',
        btn_text_cancel: 'Cancel',
        on_ok: () => this.doUnarchive(),
      });
    },

    doUnarchive() {
      this.$rc.rest({
        spinner: true,
        endpoint: 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/unarchive',
        params: {
          order_confirmation_settings_id: this.order_confirmation_settings_id,
        },
        on_success: () => {
          window.location.reload();

        },
        on_error: (error) => {
          //On okay of alert refresh the page
          this.$rc.alert('Error', error.message || 'Unable to unarchive settings', () => {
            window.location.reload();
          });

        },
      });
    },

    handlePublish(callbacks) {
      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/publish',
        params: {
          order_confirmation_settings_id: this.order_confirmation_settings_id,
          specimen: this.$refs['publish-agreement'].getSpecimen(),
        },
        on_success: () => {
          this.loadFormAndEditStatus();
          callbacks.onSuccess();
          
          this.$nextTick(() => {
            this.suggestAutomatedMessageSetup();
          });
        },
        on_error: error => {
          callbacks.onError(error.message || 'Failed to publish form. Please try again.');
        },
      });
    },

    handleDiscardChanges(callbacks) {
      this.$rc.rest({
        spinner: true,
        endpoint: 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/discard-changes',
        params: {
          order_confirmation_settings_id: this.order_confirmation_settings_id,
        },
        on_success: () => {
          this.$rc.snackbar('Changes discarded successfully!');
          this.loadFormAndEditStatus();
          callbacks.onSuccess?.();
        },
        on_error: (error) => {
          callbacks.onError?.(error);
          this.$rc.snackbar('Failed to discard changes. Please try again.');
        },
      });
    },

    validateForm() {
      this.validation_warnings = [];
      if (!this.settings) return;

      if (!this.settings.internal_label || this.settings.internal_label.trim() === '') {
        this.validation_warnings.push(
          this.mixinCreateWarning('A Form Name is required.', {
            tab: 0,
            panel: 0,
            target: 'formNameInput',
          })
        );
      }

      if (!this.settings.update_order_inbox_id) {
        this.validation_warnings.push(
          this.mixinCreateWarning('An inbox must be selected to receive order change requests.', {
            tab: 0,
            panel: 1,
            target: 'updateOrderInboxSelect',
          })
        );
      }

      const afterSubmitMsgEnabled =
        this.settings.no_double_opt_in_after_submission_message_enabled;
      if (afterSubmitMsgEnabled && !this.settings.send_from_endpoint) {
        this.validation_warnings.push(
          this.mixinCreateWarning('A "Send messages from" number is required for messaging.', {
            tab: 0,
            panel: 1,
            target: 'sendFromEndpointSelect',
          })
        );
      }

      if (
        this.settings.no_double_opt_in_after_submission_message_enabled &&
        this.settings.no_double_opt_in_after_submission_message_patterns.length
      ) {
        if (
          this.settings.no_double_opt_in_after_submission_message_patterns.every(
            pattern => !pattern || pattern.trim().length === 0
          )
        ) {
          this.validation_warnings.push(
            this.mixinCreateWarning('After submission message is required.', {
              tab: 0,
              panel: 1,
              target: 'afterSubmitInput',
            })
          );
        }
      }

      if (this.settings.use_logo && !this.settings.logo_image.url) {
        this.validation_warnings.push(
          this.mixinCreateWarning('Logo is enabled, but no logo image is set.', {
            tab: 1,
            panel: 2,
            target: 'logoImageInput',
          })
        );
      }

      if (
        this.settings.thank_you_changes_requested_contact_text &&
        this.settings.thank_you_changes_requested_contact_text.text &&
        this.settings.thank_you_changes_requested_contact_text.text.includes('{enter-contact-info}')
      ) {
        this.validation_warnings.push(
          this.mixinCreateWarning(
            "Please replace the placeholder contact information for change requests.",
            {
              tab: 1, // Content & Styles tab
              panel: 5, // Thank You Page panel
              target: 'contactTextChangesRequestedInput',
            }
          )
        );
      }
    },

    fixWarning(warning) {
      this.mixinGuideToFix(warning, {
        active_tab_model: 'active_tab',
        panel_model: warning.location.tab === 0 ? 'expanded_general' : 'expanded_styles',
      });
    },

    sendFormDataToWidget() {
      if (!this.$refs['preview-iframe'] || !this.settings) {
        return;
      }

      const settings = composeOrderConfirmationPreviewParams({
        settings: this.settings,
        is_test_submission: true,
        preview_mode: this.preview_mode,
        page: this.current_page,
      });

      sendSettingsToWidget(this.$refs['preview-iframe'], settings);
    },

    onPreviewLoad() {
      this.is_preview_loading = false;

      this.$nextTick(() => {
        this.sendFormDataToWidget();
      });
    },

    onPageChange(page) {
      this.current_page = page;

      this.$nextTick(() => {
        if (this.$refs['preview-iframe']) {
          sendPageChangeToWidget(this.$refs['preview-iframe'], page);
        }
      });
    },

    onPreviewModeChange(mode) {
      this.preview_mode = mode;
    },

    onAutoFillTestData(recycle_data = false) {
      this.$nextTick(() => {
        if (this.$refs['preview-iframe']) {
          sendAutoFillTestData(this.$refs['preview-iframe'], recycle_data);
        }
      });
    },

    onDismissFloatingControl() {
      this.floating_control_visible = false;
    },

    getDefaultEmailAgreementPattern() {
      if (this.settings.email_consent_gathering_state === 'consent-via-field-submission') {
        return this.default_legal_patterns.email_agreement_implied;
      } else if (this.settings.email_consent_gathering_state === 'checkbox-consent') {
        return this.default_legal_patterns.email_agreement_checkbox;
      }
      return this.default_legal_patterns.email_agreement_implied;
    },

    getDefaultPhoneAgreementPattern() {
      if (this.settings.phone_number_consent_gathering_state === 'consent-via-field-submission') {
        return this.default_legal_patterns.phone_agreement_implied;
      } else if (this.settings.phone_number_consent_gathering_state === 'checkbox-consent') {
        return this.default_legal_patterns.phone_agreement_checkbox;
      }
      return this.default_legal_patterns.phone_agreement_implied;
    },

    resetEmailAgreementToDefault() {
      const default_pattern = this.getDefaultEmailAgreementPattern();
      if (default_pattern) {
        this.settings.email_consent_agreement_custom_patterns = [default_pattern];
        this.onChange();
      }
    },

    resetPhoneAgreementToDefault() {
      const default_pattern = this.getDefaultPhoneAgreementPattern();
      if (default_pattern) {
        this.settings.phone_number_consent_agreement_custom_patterns = [default_pattern];
        this.onChange();
      }
    },

    get_default_double_opt_in_message_patterns() {
      return this.default_legal_patterns.double_opt_in_pattern
        ? [this.default_legal_patterns.double_opt_in_pattern]
        : ['Reply YES to confirm your subscription.'];
    },

    get_default_after_double_opt_in_message_patterns() {
      return this.default_legal_patterns.after_double_opt_in_message
        ? [this.default_legal_patterns.after_double_opt_in_message]
        : ["You're confirmed! Thanks for joining."];
    },

    get_default_no_double_opt_in_after_submission_message_patterns() {
      return this.default_legal_patterns.no_double_opt_in_after_submission_message
        ? [this.default_legal_patterns.no_double_opt_in_after_submission_message]
        : ["Thank you for confirming your order! You're now subscribed to our updates."];
    },

    loadDefaultLegalPatterns() {
      this.$rc.rest(
        'GET: /api/v1/order-confirmation/default-legal-patterns',
        {},
        data => {
          this.default_legal_patterns = {
            double_opt_in_pattern: data.double_opt_in_pattern || '',
            after_double_opt_in_message: data.after_double_opt_in_message || '',
            no_double_opt_in_after_submission_message: data.no_double_opt_in_after_submission_message || '',
            email_agreement_implied: data.email_agreement_implied || '',
            email_agreement_checkbox: data.email_agreement_checkbox || '',
            phone_agreement_implied: data.phone_agreement_implied || '',
            phone_agreement_checkbox: data.phone_agreement_checkbox || '',
          };
        },
        () => {
        }
      );
    },

    onEmailConsentGatheringChange() {
      this.onChange();
    },

    onPhoneConsentGatheringChange() {
      this.onChange();
    },

    loadAvailableLists() {
      this.$rc.rest({
        spinner: false,
        endpoint: 'LIST: /api/v1/autocomplete/lists',

        params: {},

        on_success: data => {
          let items = [];

          for (let i = 0; i < data.results.length; i++) {
            items.push({
              text: data.results[i].text,
              value: data.results[i].value,
            });
          }

          this.available_lists = items;
        },

        on_error: () => {
          this.$rc.snackbar(
            'Unable to load available lists, please try refreshing the page.',
            1500
          );
        },
      });
    },

    loadIntegrationFeatures() {
      this.checkFeatureAvailability('customers-auto-enrich-from-redchirp', is_supported => {
        this.customer_auto_enrich_feature_available = is_supported;
      });

      this.checkFeatureAvailability('tag-new-lightbox-customers-from-redchirp', is_supported => {
        this.tag_new_form_customers_available = is_supported;

        if (is_supported) {
          this.loadIntegrationTags('COMMERCE7');
        } else {
          this.integration_tags = [];
        }
      });
    },

    checkFeatureAvailability(feature_identifier, on_success) {
      this.$rc.hasEnabledIntegrationFeature(feature_identifier, on_success, () => {
        on_success(false);
      });
    },

    loadIntegrationTags(integration_type) {
      this.$rc.rest({
        spinner: false,
        endpoint:
          'GET: /api/v1/integrations/crm/tags/' +
          this.$route.params.organization_id +
          '/' +
          integration_type,

        on_success: data => {
          this.integration_tags = data.tags || [];
        },

        on_error: () => {
          this.integration_tags = [];
        },
      });
    },

    onNewCustomerTagChange(tag_id) {
      if (tag_id && tag_id !== null) {
        this.settings.new_customer_tag_integration_type = 'COMMERCE7';
      } else {
        this.settings.new_customer_tag_integration_type = null;
      }
      this.onChange();
    },

    getOrderConfAutomatedMessage() {
      this.$rc.rest({
          spinner: false,
          endpoint: 'PUT: /api/v1/auto-messages/org-profile',

          params : {
            organization_id: this.$route.params.organization_id,
          },

          on_success: (data) => {
            this.automated_message_template = data.templates.find(({ template_context }) => template_context === 'order-placed-request-order-confirmation') || null;
          },
          on_error: 'Unable to list automated messages',
      });
    },

    suggestAutomatedMessageSetup() {
      if (this.has_necessary_integration && this.automated_message_does_not_exist) {
        this.$rc.confirm({  
          title: 'Create Automated Message?',
          body: 'Your order confirmation form has been published! Would you like to create and link an automated message to send order confirmations to customers?',
          btn_text_ok: 'Create Automated Message',
          btn_text_cancel: 'Maybe Later',
          on_ok: () => this.navigateToAutomatedMessagesSettings(),
        });
      } else if (this.has_necessary_integration && this.automated_message_exists_but_not_linked) {
        this.$rc.confirm({
          title: 'Link Automated Message?',
          body: 'Your order confirmation form has been published! An "Order Placed: Request Confirmation and Opt-In (Uses Order Confirmation Form)" automated message exists, but it is not configured to use this form. Would you like to view and update it?',
          btn_text_ok: 'View Automated Message',
          btn_text_cancel: 'Maybe Later',
          on_ok: () => this.navigateToAutomatedMessageTemplate(true),
        });
      } else {
        this.$rc.snackbar('Form published successfully!', 3000);
      }
    },

    navigateToAutomatedMessageTemplate(do_highlight = false) {
      if (!this.automated_message_template) return;

      const route = {
        name: 'auto-messages-template-settings',
        params: {
          organization_id: this.$route.params.organization_id,
          template_context: 'order-placed-request-order-confirmation',
          template_subject_id: '00000000-0000-0000-0000-000000000000',
        },
      };

      if (do_highlight) {
        route.query = { highlight: 'orderConfForm' }
      }

      this.$router.push(route);
    },

    navigateToAutomatedMessagesSettings() {
      this.$router.push({
        name: 'auto-messages-settings',
        params: {
          organization_id: this.$route.params.organization_id,
        },
        query: {
          auto_add: 'true',
          template_context: 'order-placed-request-order-confirmation',
          order_confirmation_settings_id: this.order_confirmation_settings_id,
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.section-group {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.image-selector :deep(.v-input__slot) {
  margin-bottom: 0;
}
</style>